import { AppAdminFormLabel, AppButton, AppForm, AppSelect, AppTextField } from '@/components/atoms';

import AdminUserSsoFields from './AdminUserSsoFields';
import useAppToast from '@/hooks/useAppToast';
import {
  adminUserCreateFormSchema,
  createAdminUserEditFormSchema,
  isSsoPasswordRequired,
  type AdminUserFormData,
} from '@/schemas/adminUserSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import React, { useEffect } from 'react';
import { DEFAULT_SITES } from '@/constants/routeSegments';
import type { UseFormSetError } from 'react-hook-form';
import { Controller } from 'react-hook-form';

interface ManageSiteOption {
  label: string;
  value: string;
}

interface AdminUserFormProps {
  isEdit?: boolean;

  manageSiteOptions: ManageSiteOption[];
  isSuperAdminRoute: boolean;
  defaultManageSite?: string;
  isSubmitting: boolean;
  initialValues?: {
    name: string;
    email: string;
    manage_site: string;
    sso_systems?: AdminUserFormData['sso_systems'];
    sso_password_optional_systems?: string[];
    sso_login_id?: string | null;
  };
  onSubmit: (data: AdminUserFormData, setError: UseFormSetError<AdminUserFormData>) => void;
}

const AdminUserForm: React.FC<AdminUserFormProps> = ({
  isEdit = false,
  manageSiteOptions,
  isSuperAdminRoute,
  defaultManageSite = '',
  isSubmitting,
  initialValues,
  onSubmit,
}) => {
  const toast = useAppToast();

  const passwordOptionalSystems = initialValues?.sso_password_optional_systems
    ?? initialValues?.sso_systems ?? [];
  const schema = isEdit
    ? createAdminUserEditFormSchema(passwordOptionalSystems, initialValues?.manage_site, initialValues?.sso_systems)
    : adminUserCreateFormSchema;

  const {
    control,
    handleSubmit,
    setError,
    watch,
    clearErrors,
    resetField,
    getValues,
    setValue,
    formState: { errors },
  } = useTrimmedForm<AdminUserFormData>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: initialValues?.name ?? '',
      email: initialValues?.email ?? '',
      password: '',
      manage_site: initialValues?.manage_site ?? (isSuperAdminRoute ? '' : defaultManageSite),
      sso_systems: initialValues?.sso_systems ?? [],
      sso_login_id: initialValues?.sso_login_id ?? '',
    },
  });

  // The SSO block follows the site type currently selected, so on the super-admin screen it
  // appears and disappears as the dropdown changes.
  const selectedManageSite = watch('manage_site');
  // New/re-enabled destinations use delivery evidence; unchanged active selections remain
  // editable while sync is pending. Explicit SSO retry has its own missing-password response.
  const selectedSsoSystems = watch('sso_systems');
  const ssoPasswordRequired = isSsoPasswordRequired(
    selectedSsoSystems,
    initialValues?.manage_site && selectedManageSite !== initialValues.manage_site ? [] : passwordOptionalSystems,
    initialValues?.manage_site && selectedManageSite !== initialValues.manage_site ? [] : initialValues?.sso_systems,
  );

  // Unmounting the block does not clear it: react-hook-form keeps the values of unmounted fields
  // (`shouldUnregister` is false by default), so without this the request still carries whatever
  // was ticked before the site type changed -- and a super admin would get SSO links the backend
  // now rejects, or a validation error on a field no longer on screen.
  useEffect(() => {
    if (selectedManageSite === DEFAULT_SITES.SUPER_ADMIN) {
      resetField('sso_systems', { defaultValue: [] });
      resetField('sso_login_id', { defaultValue: '' });
      return;
    }

    // 審査システム (P2) serves メディックス only, so a tick left over from a previous site type has
    // to be dropped for the same reason the super-admin branch above clears the whole block:
    // disabling a checkbox does not clear it, and the request would still carry the value.
    // Sending it anyway does not fail loudly -- P2 accepts the account row through Common Sync and
    // only the identity link is refused, into a log line nobody reads.
    if (selectedManageSite !== DEFAULT_SITES.MEDIX) {
      const ticked = getValues('sso_systems') ?? [];

      if (ticked.includes('P2')) {
        setValue(
          'sso_systems',
          ticked.filter(system => system !== 'P2'),
          { shouldDirty: true },
        );
      }
    }
  }, [selectedManageSite, resetField, getValues, setValue]);

  const handleFormSubmit = (data: AdminUserFormData) => {
    onSubmit(data, setError);
  };

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (type !== 'change' || !name) return;
      clearErrors(name as keyof AdminUserFormData);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  return (
    <AppForm
      onFinish={handleSubmit(handleFormSubmit, () => toast.error('エラーが発生しました。'))}
      layout="vertical"
      // Neo cho e2e: trên màn list có tới bốn AppModal cùng tồn tại trong DOM, nên mọi truy vấn
      // của test phải bám vào form đang mở chứ không phải cả trang.
      data-testid={isEdit ? 'admin-user-form-edit' : 'admin-user-form-create'}
    >
      <div className="space-y-4">
        {isSuperAdminRoute && (
          <div>
            <AppAdminFormLabel label="サイト区分" required />
            <Controller
              name="manage_site"
              control={control}
              render={({ field }) => (
                <AppSelect
                  {...field}
                  placeholder=""
                  value={field.value || undefined}
                  onChange={value => field.onChange(value ?? '')}
                  options={manageSiteOptions}
                  className="w-full"
                  status={errors.manage_site ? 'error' : undefined}
                />
              )}
            />
            {errors.manage_site && (
              <p className="text-red-500 text-sm mt-1">{errors.manage_site.message}</p>
            )}
          </div>
        )}

        <div>
          <AppAdminFormLabel label="管理者名" required />
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                className="w-full"
                maxLength={20}
                data-testid="admin-form-name"
                status={errors.name ? 'error' : undefined}
              />
            )}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <AppAdminFormLabel label="メールアドレス" required />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                type="email"
                className="w-full"
                maxLength={100}
                autoComplete="off"
                data-testid="admin-form-email"
                status={errors.email ? 'error' : undefined}
              />
            )}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <AppAdminFormLabel label="パスワード" required={!isEdit || ssoPasswordRequired} />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                value={field.value ?? ''}
                type="password"
                className="w-full"
                autoComplete="new-password"
                data-testid="admin-form-password"
                status={errors.password ? 'error' : undefined}
              />
            )}
          />
          {ssoPasswordRequired && (
            <p data-testid="admin-form-password-sso-help" className="text-xs text-gray-500 mt-1">
              SSO連携を行うため、パスワードを再入力してください。
            </p>
          )}
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
          )}
        </div>

        <AdminUserSsoFields control={control} errors={errors} manageSite={selectedManageSite} />
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <AppButton type="primary" htmlType="submit" loading={isSubmitting}>
          更新
        </AppButton>
      </div>
    </AppForm>
  );
};

export default AdminUserForm;
